import { defineStore } from "pinia";
import axios from "axios";
import { API_URL } from "@/config/api";
import { consumeConfirmedReason } from "@/utils/confirmed-action";
import { validateContentSnapshot, validationMessage } from "@/utils/public-content-validation";

const messageFor = (error, fallback) => {
  if (error.response?.status === 503) return "公開コンテンツ機能は現在停止しています。時間をおいてお試しください。";
  if (error.response?.status === 404) return "お探しのページは見つかりませんでした。";
  if (error.response?.status === 410) return "この内容は現在公開されていません。";
  return error.response?.data?.detail || fallback;
};

export const usePublicContentStore = defineStore("publicContent", {
  state: () => ({ items: [], current: null, adminItems: [], auditLogs: [], loading: false, actionPending: false, error: null, success: null, statusCode: null, deniedActions: {}, validationErrors: [], conflictBackup: null, backupVisible: false }),
  actions: {
    async fetchList(kind) {
      this.loading = true; this.error = null; this.statusCode = null;
      const path = kind === "model_course" ? "model-courses" : kind === "ranking" ? "rankings" : "contents";
      try { this.items = (await axios.get(`${API_URL}/public/${path}`, { params: { page: 1, page_size: 20 } })).data; }
      catch (error) { this.statusCode = error.response?.status; this.error = messageFor(error, "公開コンテンツを読み込めませんでした。"); }
      finally { this.loading = false; }
    },
    async fetchDetail(kind, slug) {
      this.loading = true; this.current = null; this.error = null; this.statusCode = null;
      const path = kind === "model_course" ? "model-courses" : "rankings";
      try { this.current = (await axios.get(`${API_URL}/public/${path}/${slug}`)).data; return this.current; }
      catch (error) { this.statusCode = error.response?.status; this.error = messageFor(error, "内容を読み込めませんでした。"); return null; }
      finally { this.loading = false; }
    },
    async copyCourse(slug, expectedPublishedVersion, idempotencyKey) {
      return (await axios.post(`${API_URL}/public/model-courses/${slug}/copy`, { expected_published_version: expectedPublishedVersion, idempotency_key: idempotencyKey })).data;
    },
    async fetchAdminList() { this.loading = true; this.error = null; this.statusCode = null; try { this.adminItems = (await axios.get(`${API_URL}/admin/public-contents`)).data; } catch (e) { this.statusCode = e.response?.status; this.error = messageFor(e, "一覧を読み込めませんでした。"); } finally { this.loading = false; } },
    async fetchAdminDetail(id) { this.loading = true; this.error = null; this.statusCode = null; try { this.current = (await axios.get(`${API_URL}/admin/public-contents/${id}`)).data; this.auditLogs = (await axios.get(`${API_URL}/admin/public-contents/${id}/audit-logs`)).data; return this.current; } catch (e) { this.statusCode = e.response?.status; this.error = messageFor(e, "詳細を読み込めませんでした。"); } finally { this.loading = false; } },
    async createContent(payload) {
      this.error = null; this.success = null;
      const validationErrors = validateContentSnapshot(payload.snapshot); this.validationErrors = validationErrors; if (validationErrors.length) { const error = new Error(validationMessage(validationErrors)); error.validationErrors = validationErrors; throw error; }
      try { const result = (await axios.post(`${API_URL}/admin/public-contents`, payload)).data; this.success = "公開コンテンツの下書きを作成しました。"; return result; }
      catch (e) { this.error = messageFor(e, "コンテンツを作成できませんでした。"); throw e; }
    },
    async saveDraft(id, payload, useRevision) {
      this.error = null;
      const validationErrors = validateContentSnapshot(payload.snapshot); if (validationErrors.length) { const error = new Error(validationMessage(validationErrors)); error.validationErrors = validationErrors; throw error; }
      const url = `${API_URL}/admin/public-contents/${id}/${useRevision ? "revisions" : "draft"}`;
      try {
        const response = useRevision ? await axios.post(url, payload) : await axios.put(url, payload);
        await this.fetchAdminDetail(id); this.success = "作業版を保存しました。";
        return response.data;
      } catch (e) {
        if (e.response?.status === 403) this.error = "編集権限がありません。";
        else if (e.response?.status === 409) { this.error = "内容または審査状態が更新されています。再読み込みしてください。"; this.conflictBackup = JSON.stringify(payload, null, 2); this.backupVisible = false; }
        else this.error = messageFor(e, "変更を保存できませんでした。");
        throw e;
      }
    },
    async adminAction(id, action, payload) {
      if (this.actionPending) return;
      this.actionPending = true;
      this.success = null;
      const actionPayload = action === "withdraw" ? { ...payload, reason: consumeConfirmedReason() } : payload;
      const messages = { submit: "審査へ提出しました。", reviews: "審査結果を記録しました。", publish: "公開しました。", withdraw: "公開を取り下げました。", republish: "復公開しました。" };
      try { await axios.post(`${API_URL}/admin/public-contents/${id}/${action}`, actionPayload); await this.fetchAdminDetail(id); this.success = messages[action] || "操作を完了しました。"; }
      catch (e) { if (e.response?.status === 403) { this.deniedActions[`${id}:${action}`] = true; this.error = "このコンテンツで、この操作を行う権限がありません。"; } else if (e.response?.status === 409) this.error = "内容が更新されています。再読み込みして確認してください。"; else this.error = messageFor(e, "操作を完了できませんでした。"); throw e; }
      finally { this.actionPending = false; }
    },
    resetDeniedActions(id) { Object.keys(this.deniedActions).filter((key) => key.startsWith(`${id}:`)).forEach((key) => delete this.deniedActions[key]); },
    showConflictBackup() { this.backupVisible = true; this.success = "競合前の入力内容を画面内へ退避しました。"; },
  },
});
